import { useCallback, useEffect, useState, type FormEvent } from "react";
import { apiFetch } from "../services/apiUtils";
import { useAuth } from "../contexts/AuthContext";
import { EmptyState, StatusBadge, SubTabs } from "./leads-contacts/ui";
import {
  CheckResultPanel,
  DncAuditTable,
  DncNotActive,
  DncOverviewCards,
  DncRecordDrawer,
  DncRecordsTable,
} from "./leads-contacts/DncViews";
import {
  canViewDncRecords,
  channelsLabel,
  dncUiEnabled,
  formatDncDate,
  nationalRegistryWorkflowSteps,
  type DncAuditEvent,
  type DncEligibility,
  type DncRecord,
  type DncSummary,
} from "./leads-contacts/dncModel";

const inputClass = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800";
const labelClass = "text-xs font-semibold uppercase tracking-wide text-slate-500";
const primaryButton = "rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40";
const secondaryButton = "rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40";

type LoadState = "idle" | "loading" | "ready" | "disabled" | "unavailable" | "forbidden";

function useDncApi() {
  const { token, activeContext } = useAuth();
  const contextId = activeContext?.id;
  return useCallback(
    (path: string, init: RequestInit = {}) =>
      apiFetch(`/api/leads-contacts/compliance${path}`, {
        ...init,
        headers: {
          // apiFetch's default Content-Type is dropped when custom headers are supplied.
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(contextId ? { "X-Active-Context": contextId } : {}),
        },
      }),
    [token, contextId],
  );
}

function useDncAdmin(): boolean {
  const { isRegionalAdmin, activeContext } = useAuth();
  return canViewDncRecords(Boolean(isRegionalAdmin), activeContext?.id);
}

export function CpaDncWorkspace(): JSX.Element {
  const isAdmin = useDncAdmin();
  const items = ["Overview", "Check Before Contacting", ...(isAdmin ? ["Marketing Preferences"] : [])];
  const [active, setActive] = useState("Overview");
  const current = items.includes(active) ? active : "Overview";

  return (
    <div className="space-y-4">
      <SubTabs items={items} active={current} onChange={setActive} />
      {current === "Overview" ? <DncOverview isAdmin={isAdmin} onCheck={() => setActive("Check Before Contacting")} /> : null}
      {current === "Check Before Contacting" ? <CheckBeforeContacting /> : null}
      {current === "Marketing Preferences" ? <DncRecords /> : null}
    </div>
  );
}

function DncOverview({ isAdmin, onCheck }: { isAdmin: boolean; onCheck: () => void }): JSX.Element {
  const request = useDncApi();
  const [summary, setSummary] = useState<DncSummary | null>(null);
  const [state, setState] = useState<LoadState>(dncUiEnabled() ? "idle" : "disabled");

  useEffect(() => {
    if (!dncUiEnabled() || !isAdmin) return;
    let cancelled = false;
    setState("loading");
    request("/admin/summary")
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 404) return setState("disabled");
        const body = (await response.json().catch(() => null)) as DncSummary | null;
        if (!response.ok || !body?.available) return setState("unavailable");
        setSummary(body);
        setState("ready");
      })
      .catch(() => { if (!cancelled) setState("unavailable"); });
    return () => { cancelled = true; };
  }, [isAdmin, request]);

  return (
    <div className="space-y-4">
      <section className="surface-card flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Check Before Contacting</h2>
          <p className="mt-1 text-sm text-slate-500">Search a phone number or email address before direct marketing.</p>
        </div>
        <button type="button" onClick={onCheck} className={primaryButton}>Open check</button>
      </section>

      <section className="surface-card space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Suppression summary</h2>
            <p className="mt-1 text-sm text-slate-500">Current marketing preferences and registry status.</p>
          </div>
          {state === "ready" ? <StatusBadge label="Live data" tone="info" /> : null}
        </div>
        {state === "disabled" ? <DncNotActive reason="disabled" /> : null}
        {state === "unavailable" ? <DncNotActive reason="unavailable" /> : null}
        {state === "loading" ? <p className="text-sm text-slate-500" role="status">Loading summary…</p> : null}
        {state === "ready" && summary ? <DncOverviewCards summary={summary} /> : null}
        {state === "idle" && !isAdmin ? (
          <EmptyState title="Summary figures are restricted" detail="Suppression totals are available to Regional Compliance administrators. Use Check Before Contacting for individual numbers or email addresses." />
        ) : null}
      </section>

      <section className="surface-card p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">National Registry cleansing</h2>
            <p className="mt-1 text-sm text-slate-500">Monthly workflow. No files can be generated, submitted or processed yet.</p>
          </div>
          <StatusBadge label="Not active" />
        </div>
        <ol className="mt-4 flex flex-wrap gap-2">
          {nationalRegistryWorkflowSteps.map((step, index) => (
            <li key={step} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
              <span className="mr-1 text-slate-400">{String(index + 1).padStart(2, "0")}</span>
              {step}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export function CheckBeforeContacting(): JSX.Element {
  const request = useDncApi();
  const [identifier, setIdentifier] = useState("");
  const [checked, setChecked] = useState("");
  const [result, setResult] = useState<DncEligibility | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(!dncUiEnabled());

  const check = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = identifier.trim();
    if (!value) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await request("/check", { method: "POST", body: JSON.stringify({ identifier: value }) });
      if (response.status === 404) {
        setDisabled(true);
        return;
      }
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error ?? "The check could not be completed. Treat as REVIEW REQUIRED.");
      setResult(body as DncEligibility);
      setChecked(value);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The check could not be completed. Treat as REVIEW REQUIRED.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="surface-card space-y-4 p-5">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Check Before Contacting</h2>
        <p className="mt-1 text-sm text-slate-600">Search a phone number or email address before direct marketing.</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          MAPP checks the compliance data currently available to KWSA. A CLEAR result means no applicable suppression was found in the data available at the time of the check.
        </p>
      </div>
      {disabled ? (
        <DncNotActive reason="disabled" />
      ) : (
        <>
          <form onSubmit={check} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className={`min-w-0 flex-1 ${labelClass}`} htmlFor="dnc-check-identifier">
              Phone number or email address
              <input
                id="dnc-check-identifier"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder="e.g. 082 123 4567 or name@example.com"
                maxLength={320}
                autoComplete="off"
                className={`${inputClass} h-11 text-base`}
              />
            </label>
            <button type="submit" disabled={loading || !identifier.trim()} className={`${primaryButton} h-11 px-6`}>
              {loading ? "Checking…" : "Check"}
            </button>
          </form>
          {error ? <p role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</p> : null}
          {result ? <CheckResultPanel result={result} identifier={checked} /> : null}
        </>
      )}
    </section>
  );
}

type RecordFilters = { search: string; scope: string; channel: string; from: string; to: string };
const emptyFilters: RecordFilters = { search: "", scope: "", channel: "", from: "", to: "" };

function filterBody(filters: RecordFilters, offset: number) {
  return {
    limit: 50,
    offset,
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.scope ? { scope: filters.scope } : {}),
    ...(filters.channel ? { channel: filters.channel } : {}),
    ...(filters.from ? { from: new Date(`${filters.from}T00:00:00.000Z`).toISOString() } : {}),
    ...(filters.to ? { to: new Date(`${filters.to}T23:59:59.999Z`).toISOString() } : {}),
  };
}

function DncRecords(): JSX.Element {
  const request = useDncApi();
  const [filters, setFilters] = useState<RecordFilters>(emptyFilters);
  const [records, setRecords] = useState<DncRecord[]>([]);
  const [state, setState] = useState<LoadState>(dncUiEnabled() ? "idle" : "disabled");
  const [selected, setSelected] = useState<DncRecord | null>(null);

  const load = useCallback(async (offset: number, current: RecordFilters) => {
    setState("loading");
    try {
      const response = await request("/admin/records/search", { method: "POST", body: JSON.stringify(filterBody(current, offset)) });
      if (response.status === 404) return setState("disabled");
      if (response.status === 403) return setState("forbidden");
      const body = await response.json().catch(() => null);
      if (!response.ok || !body?.available) return setState("unavailable");
      setRecords((previous) => (offset === 0 ? body.items : [...previous, ...body.items]));
      setState("ready");
    } catch {
      setState("unavailable");
    }
  }, [request]);

  useEffect(() => {
    if (dncUiEnabled()) void load(0, emptyFilters);
  }, [load]);

  const update = (key: keyof RecordFilters) => (event: { target: { value: string } }) => setFilters((current) => ({ ...current, [key]: event.target.value }));

  if (state === "disabled") return <section className="surface-card p-5"><DncNotActive reason="disabled" /></section>;
  if (state === "forbidden") return <section className="surface-card p-5"><EmptyState title="Restricted" detail="Marketing preference records are available to Regional Compliance administrators in Regional scope." /></section>;

  return (
    <div className="space-y-4">
      <form
        className="surface-card grid gap-3 p-4 md:grid-cols-[minmax(220px,1.4fr)_160px_160px_140px_140px_auto] md:items-end"
        onSubmit={(event) => { event.preventDefault(); void load(0, filters); }}
      >
        <label className={labelClass}>Search<input value={filters.search} onChange={update("search")} maxLength={160} placeholder="Name, phone or email" className={inputClass} /></label>
        <label className={labelClass}>Scope<select value={filters.scope} onChange={update("scope")} className={inputClass}><option value="">All scopes</option><option value="KWSA">KWSA-wide</option><option value="ASSOCIATE">Associate-specific</option></select></label>
        <label className={labelClass}>Channel<select value={filters.channel} onChange={update("channel")} className={inputClass}><option value="">All channels</option><option value="ALL_MARKETING">All marketing</option><option value="EMAIL">Email</option><option value="SMS">SMS</option><option value="TELEPHONE">Telephone</option><option value="WHATSAPP">WhatsApp</option></select></label>
        <label className={labelClass}>From<input type="date" value={filters.from} onChange={update("from")} className={inputClass} /></label>
        <label className={labelClass}>To<input type="date" value={filters.to} onChange={update("to")} className={inputClass} /></label>
        <button type="submit" disabled={state === "loading"} className={`${primaryButton} h-[38px]`}>Search</button>
      </form>
      <section className="surface-card overflow-hidden">
        {state === "unavailable" ? <div className="p-5"><DncNotActive reason="unavailable" /></div> : null}
        {state === "loading" && records.length === 0 ? <p className="p-5 text-sm text-slate-500" role="status">Loading records…</p> : null}
        {state !== "unavailable" && records.length > 0 ? <DncRecordsTable records={records} onOpen={setSelected} /> : null}
        {state === "ready" && records.length === 0 ? <div className="p-5"><EmptyState title="No marketing preference records found" detail="Adjust the filters or check again later." /></div> : null}
      </section>
      {records.length > 0 && records.length % 50 === 0 ? (
        <button type="button" disabled={state === "loading"} onClick={() => void load(records.length, filters)} className={secondaryButton}>Load more</button>
      ) : null}
      {selected ? <RecordDrawer record={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function RecordDrawer({ record, onClose }: { record: DncRecord; onClose: () => void }): JSX.Element {
  const request = useDncApi();
  const identifier = record.normalized_identifiers?.[0] ?? record.identifiers?.[0] ?? "";
  const [status, setStatus] = useState<DncEligibility | null>(null);
  const [history, setHistory] = useState<DncAuditEvent[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!identifier) return;
    let cancelled = false;
    Promise.all([
      request("/check", { method: "POST", body: JSON.stringify({ identifier }) }).then((response) => (response.ok ? response.json() : null)),
      request("/admin/events/search", { method: "POST", body: JSON.stringify({ search: identifier, limit: 50, offset: 0 }) }).then((response) => (response.ok ? response.json() : null)),
    ])
      .then(([eligibility, events]) => {
        if (cancelled) return;
        setStatus(eligibility as DncEligibility | null);
        setHistory(events?.available ? (events.items as DncAuditEvent[]) : []);
        setFailed(!eligibility);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [identifier, request]);

  return (
    <DncRecordDrawer
      record={record}
      onClose={onClose}
      currentStatus={status ? <CheckResultPanel result={status} identifier={identifier} /> : failed ? <p className="text-sm text-slate-600">Current status could not be loaded. Treat as REVIEW REQUIRED.</p> : <p className="text-sm text-slate-500" role="status">Checking current status…</p>}
      history={history === null ? <p className="text-sm text-slate-500" role="status">Loading history…</p> : history.length === 0 ? <p className="text-sm text-slate-600">No further events recorded for this identifier.</p> : (
        <ol className="space-y-2 text-sm text-slate-700">
          {history.map((event) => (
            <li key={event.id} className="flex flex-wrap justify-between gap-2 border-b border-slate-200 pb-2 last:border-0 last:pb-0">
              <span><span className="font-semibold">{event.action}</span> · {channelsLabel(event.channel ? [event.channel] : null)} · {event.suppression_scope === "ASSOCIATE" ? `Associate-specific (KWUID ${event.target_agent_kwuid ?? "unknown"})` : "KWSA-wide"}</span>
              <span className="text-xs text-slate-500">{formatDncDate(event.recorded_at)}</span>
            </li>
          ))}
        </ol>
      )}
    />
  );
}

export function DncAuditLog(): JSX.Element {
  const request = useDncApi();
  const isAdmin = useDncAdmin();
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [action, setAction] = useState("");
  const [agentKwuid, setAgentKwuid] = useState("");
  const [marketCentreId, setMarketCentreId] = useState("");
  const [events, setEvents] = useState<DncAuditEvent[]>([]);
  const [state, setState] = useState<LoadState>(dncUiEnabled() ? "idle" : "disabled");

  const load = async (offset: number) => {
    setState("loading");
    try {
      const response = await request("/admin/events/search", {
        method: "POST",
        body: JSON.stringify({
          limit: 50,
          offset,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(source ? { source } : {}),
          ...(action ? { action } : {}),
          ...(agentKwuid.trim() ? { agentKwuid: agentKwuid.trim() } : {}),
          ...(marketCentreId.trim() ? { marketCentreId: marketCentreId.trim() } : {}),
        }),
      });
      if (response.status === 404) return setState("disabled");
      if (response.status === 403) return setState("forbidden");
      const body = await response.json().catch(() => null);
      if (!response.ok || !body?.available) return setState("unavailable");
      setEvents((current) => (offset === 0 ? body.items : [...current, ...body.items]));
      setState("ready");
    } catch {
      setState("unavailable");
    }
  };

  if (state === "disabled") return <section className="surface-card p-5"><DncNotActive reason="disabled" /></section>;
  if (!isAdmin || state === "forbidden") {
    return <section className="surface-card p-5"><EmptyState title="Restricted" detail="The DNC audit log is available to Regional Compliance administrators in Regional scope." /></section>;
  }

  return (
    <div className="space-y-4">
      <section className="surface-card p-5">
        <h2 className="text-lg font-semibold text-slate-900">DNC audit log</h2>
        <p className="mt-1 text-sm text-slate-500">Every recorded DNC event, including registry and administrator changes. Searches are audited.</p>
      </section>
      <form
        className="surface-card grid gap-3 p-4 md:grid-cols-[minmax(220px,1.4fr)_160px_140px_140px_160px_auto] md:items-end"
        onSubmit={(event) => { event.preventDefault(); void load(0); }}
      >
        <label className={labelClass}>Name or identifier<input value={search} onChange={(event) => setSearch(event.target.value)} maxLength={160} className={inputClass} /></label>
        <label className={labelClass}>Source<select value={source} onChange={(event) => setSource(event.target.value)} className={inputClass}><option value="">All sources</option><option value="INTERNAL">Internal</option><option value="NATIONAL_REGISTRY">National Registry</option></select></label>
        <label className={labelClass}>Event<select value={action} onChange={(event) => setAction(event.target.value)} className={inputClass}><option value="">All events</option><option value="ADDED">Added</option><option value="REMOVED">Removed</option><option value="UPDATED">Updated</option></select></label>
        <label className={labelClass}>Origin KWUID<input value={agentKwuid} onChange={(event) => setAgentKwuid(event.target.value)} maxLength={20} className={inputClass} /></label>
        <label className={labelClass}>Origin MC ID<input value={marketCentreId} onChange={(event) => setMarketCentreId(event.target.value)} maxLength={80} className={inputClass} /></label>
        <button type="submit" disabled={state === "loading"} className={`${primaryButton} h-[38px]`}>Search</button>
      </form>
      <section className="surface-card overflow-hidden">
        {state === "idle" ? <div className="p-5"><EmptyState title="Search the audit log" detail="Apply filters and search to view DNC events." /></div> : null}
        {state === "unavailable" ? <div className="p-5"><DncNotActive reason="unavailable" /></div> : null}
        {state === "loading" && events.length === 0 ? <p className="p-5 text-sm text-slate-500" role="status">Searching…</p> : null}
        {state !== "unavailable" && events.length > 0 ? <DncAuditTable events={events} /> : null}
        {state === "ready" && events.length === 0 ? <div className="p-5"><EmptyState title="No DNC events found" detail="Adjust the filters and search again." /></div> : null}
      </section>
      {events.length > 0 && events.length % 50 === 0 ? (
        <button type="button" disabled={state === "loading"} onClick={() => void load(events.length)} className={secondaryButton}>Load more</button>
      ) : null}
    </div>
  );
}
